import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Canvas, useFrame } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { apiFetch } from '../../../../lib/api';

type OverviewTimeframe = 'day' | 'week' | 'month' | 'quarter' | 'year';

type KeyValueDistribution = {
  key: string;
  label: string;
  value: number;
};

type TeamDistribution = {
  id: string;
  label: string;
  total: number;
  active: number;
  closed: number;
  members: number;
  availableMinutes: number;
  scheduledMinutes: number;
  totalEstimatedMinutes: number;
  remainingEstimateMinutes: number;
  activeMinutes: number;
  blockedMinutes: number;
  withinTargetCount: number;
  outsideTargetCount: number;
};

type ProjectDistribution = {
  id: string;
  label: string;
  total: number;
  active: number;
  closed: number;
  breached: number;
  totalEstimatedMinutes: number;
  remainingEstimateMinutes: number;
  activeMinutes: number;
};

type AgentDistribution = {
  userId: string;
  uid: string | null;
  name: string;
  email: string | null;
  photoURL: string | null;
  total: number;
  active: number;
  closed: number;
  breached: number;
  avgResolutionHours: number;
  avgActiveResolutionHours: number;
  totalEstimatedMinutes: number;
  remainingEstimateMinutes: number;
  activeMinutes: number;
  blockedMinutes: number;
  withinTargetCount: number;
  outsideTargetCount: number;
  availableMinutes: number;
  scheduledMinutes: number;
};

type TimeseriesEntry = {
  key: string;
  label: string;
  startAt: string;
  endAt: string;
  created: number;
  closed: number;
  touched: number;
  comments: number;
  transitions: number;
};

type TicketSample = {
  id: string;
  ticketNumber: number | null;
  title: string;
  orgName: string;
  projectName: string | null;
  teamLabel: string | null;
  status: string;
  priority: string;
  queueWeight: number | null;
  assignedAgentName: string | null;
  requestCategoryName?: string | null;
  estimatedMinutes?: number | null;
  remainingEstimateMinutes?: number | null;
  targetCompletionMinutes?: number | null;
  actualActiveMinutes?: number | null;
  blockedMinutes?: number | null;
  createdAt: string;
  updatedAt: string;
};

type TeamOverviewResponse = {
  timeframe: OverviewTimeframe;
  range: {
    startAt: string;
    endAt: string;
    bucketUnit: string;
    timezone: string;
  };
  scope: {
    orgIds: string[];
    orgs?: Array<{ id: string; name: string }>;
    orgId: string | null;
    teamId: string | null;
    projectId: string | null;
  };
  summary: {
    totalTickets: number;
    activeTickets: number;
    closedTickets: number;
    resolvedTickets: number;
    createdInRange: number;
    closedInRange: number;
    avgResolutionHours: number;
    medianResolutionHours: number;
    p95ResolutionHours: number;
    avgActiveResolutionHours: number;
    medianActiveResolutionHours: number;
    p95ActiveResolutionHours: number;
    activeProjects: number;
    activeAgents: number;
    slaBreachedOpen: number;
    slaBreachedResolved: number;
    slaAtRisk24h: number;
    slaAtRisk72h: number;
    reopenCount: number;
    totalQueueWeight: number;
    avgQueueWeight: number;
    trackedActiveMinutes: number;
    trackedBlockedMinutes: number;
    liveActiveMinutes: number;
    totalEstimatedMinutes: number;
    totalRemainingEstimateMinutes: number;
    targetTrackedCount: number;
    withinTargetCount: number;
    outsideTargetCount: number;
    contractedMonthlyMinutesMin: number;
    contractedMonthlyMinutesMax: number;
    remainingContractedMinutesMin: number;
    remainingContractedMinutesMax: number;
    operatorCapacityMinutes: number;
    operatorBookedMinutes: number;
    operatorUnbookedMinutes: number;
    scheduledEvents: number;
    completedEvents: number;
    canceledEvents: number;
    noShowEvents: number;
    totalMeetingHours: number;
    availableSlots: number;
    utilizationRate: number;
  };
  distributions: {
    status: KeyValueDistribution[];
    priority: KeyValueDistribution[];
    aging: KeyValueDistribution[];
    teams: TeamDistribution[];
    services: KeyValueDistribution[];
    projects: ProjectDistribution[];
    agents: AgentDistribution[];
    requestCategories: KeyValueDistribution[];
    transitionFunnel: KeyValueDistribution[];
    eventTypes: KeyValueDistribution[];
  };
  timeseries: TimeseriesEntry[];
  ticketsSample: TicketSample[];
};

type WireNode = {
  id: string;
  position: [number, number, number];
  color: string;
  scale: number;
};

type MeterItem = {
  key: string;
  label: string;
  value: number;
  meta?: string;
  color?: string;
};

const INK = '#ededeb';
const SOFT = 'rgba(237, 237, 235, 0.78)';
const DIM = 'rgba(237, 237, 235, 0.46)';
const FAINT = 'rgba(237, 237, 235, 0.16)';
const PANEL = 'rgba(12, 12, 15, 0.94)';
const PANEL_ALT = 'rgba(18, 18, 22, 0.96)';
const CYAN = '#b8f2ff';
const RED = '#ff8e8e';
const AMBER = '#f7d6a1';
const GREEN = '#c7f1d4';
const BLUE = '#c2d8ff';

const STATUS_COLORS: Record<string, string> = {
  open: CYAN,
  in_progress: BLUE,
  blocked: AMBER,
  resolved: GREEN,
  closed: SOFT,
};

const PRIORITY_COLORS: Record<string, string> = {
  P0: RED,
  P1: AMBER,
  P2: CYAN,
  P3: SOFT,
};

const TIMEFRAME_OPTIONS: Array<{ value: OverviewTimeframe; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const cornerStrokeStack = (color: string, size: number) => `
  linear-gradient(135deg, transparent calc(50% - 0.7px), ${color} calc(50% - 0.7px), ${color} calc(50% + 0.7px), transparent calc(50% + 0.7px)) top left / ${size}px ${size}px no-repeat,
  linear-gradient(225deg, transparent calc(50% - 0.7px), ${color} calc(50% - 0.7px), ${color} calc(50% + 0.7px), transparent calc(50% + 0.7px)) top right / ${size}px ${size}px no-repeat,
  linear-gradient(315deg, transparent calc(50% - 0.7px), ${color} calc(50% - 0.7px), ${color} calc(50% + 0.7px), transparent calc(50% + 0.7px)) bottom right / ${size}px ${size}px no-repeat,
  linear-gradient(45deg, transparent calc(50% - 0.7px), ${color} calc(50% - 0.7px), ${color} calc(50% + 0.7px), transparent calc(50% + 0.7px)) bottom left / ${size}px ${size}px no-repeat
`;

const drift = keyframes`
  0% { background-position: 0 0, 0 0; }
  100% { background-position: 0 120px, 120px 0; }
`;

const scan = keyframes`
  0% { transform: translateX(-130%); opacity: 0; }
  25% { opacity: 0.8; }
  100% { transform: translateX(130%); opacity: 0; }
`;

const blink = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

const shell = css<{ $accent?: string; $alert?: boolean }>`
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ $alert, $accent }) => ($alert ? 'rgba(255, 142, 142, 0.46)' : $accent ?? FAINT)};
  clip-path: polygon(16px 0, calc(100% - 16px) 0, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0 calc(100% - 16px), 0 16px);
  background:
    ${({ $alert, $accent }) => cornerStrokeStack($alert ? 'rgba(255, 142, 142, 0.82)' : $accent ?? 'rgba(237,237,235,0.54)', 16)},
    linear-gradient(180deg, ${PANEL}, ${PANEL_ALT}),
    linear-gradient(135deg, rgba(255,255,255,0.03), transparent 34%),
    linear-gradient(315deg, rgba(255,255,255,0.03), transparent 44%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
    background-size: 52px 52px, 52px 52px;
    opacity: 0.12;
  }
`;

const Page = styled.section`
  min-height: calc(100dvh - 2rem);
  padding: 14px;
  
`;

const Board = styled.div`
  position: relative;
  max-width: 1840px;
  margin: 0 auto;
  padding: 16px;
  

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
    background-size: 80px 80px, 80px 80px;
    opacity: 0.16;
    animation: ${drift} 28s linear infinite;
  }
`;

const Header = styled.header`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 10px;
  margin-bottom: 14px;
`;

const Kicker = styled.div`
  color: ${DIM};
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.28em;
  text-transform: uppercase;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  color: ${INK};
  font-size: clamp(2.2rem, 5vw, 4.4rem);
  line-height: 0.92;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 900;
`;

const HeaderRail = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.div<{ $alert?: boolean; $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid ${({ $alert, $active }) => ($alert ? 'rgba(255, 142, 142, 0.52)' : $active ? 'rgba(237,237,235,0.52)' : FAINT)};
  background: rgba(255,255,255,0.02);
  color: ${INK};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $alert, $active }) => ($alert ? RED : $active ? CYAN : SOFT)};
    animation: ${blink} 2.2s linear infinite;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 1180px;
  color: ${DIM};
  font-size: 0.86rem;
  line-height: 1.6;
  font-weight: 700;
`;

const ControlBar = styled.div<{ $accent?: string; $alert?: boolean }>`
  ${shell};
  position: relative;
  z-index: 1;
  padding: 12px;
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
`;
const ControlTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const ControlTitle = styled.div`
  color: ${INK};
  font-size: 0.86rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const TimeframeRow = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TimeframeButton = styled.button<{ $active: boolean }>`
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(237,237,235,0.52)' : FAINT)};
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)')};
  color: ${INK};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: rgba(237,237,235,0.36);
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.span`
  color: ${DIM};
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const FieldSelect = styled.select`
  min-height: 38px;
  border: 1px solid ${FAINT};
  background: rgba(255,255,255,0.02);
  color: ${INK};
  padding: 0 10px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  color-scheme: dark;

  &:focus {
    outline: none;
    border-color: rgba(237,237,235,0.42);
  }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetaBox = styled.div`
  padding: 8px 10px;
  border: 1px dashed ${FAINT};
  background: rgba(255,255,255,0.015);
  color: ${DIM};
  font-size: 0.7rem;
  line-height: 1.45;
  font-weight: 700;

  strong {
    display: block;
    color: ${INK};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 3px;
  }
`;

const Notice = styled.div<{ $alert?: boolean }>`
  ${shell};
  padding: 10px 12px;
  margin-bottom: 12px;
  color: ${({ $alert }) => ($alert ? RED : INK)};
  font-size: 0.78rem;
  font-weight: 800;
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 12px;
`;

const Plate = styled.section<{ $col: string; $row?: string; $min?: string; $accent?: string; $alert?: boolean }>`
  ${shell};
  grid-column: ${({ $col }) => $col};
  grid-row: ${({ $row }) => $row ?? 'auto'};
  min-height: ${({ $min }) => $min ?? 'auto'};

  @media (max-width: 1380px) {
    grid-column: 1 / -1;
    grid-row: auto;
    min-height: auto;
  }
`;

const PlateHead = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px 0;
`;

const PlateTitleWrap = styled.div`
  display: grid;
  gap: 4px;
`;

const PlateCode = styled.div`
  color: ${DIM};
  font-size: 0.6rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const PlateTitle = styled.h2`
  margin: 0;
  color: ${INK};
  font-size: 0.94rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const PlateCopy = styled.div`
  color: ${DIM};
  font-size: 0.72rem;
  line-height: 1.45;
  font-weight: 700;
`;

const PlateIndex = styled.div`
  color: ${SOFT};
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const PlateBody = styled.div`
  position: relative;
  z-index: 1;
  padding: 12px 14px 14px;
  display: grid;
  gap: 10px;
`;

const HeroLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
  gap: 12px;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const ScenePanel = styled.div`
  position: relative;
  min-height: 420px;
  border: 1px solid rgba(255,255,255,0.08);
  background: linear-gradient(180deg, rgba(8,8,10,0.96), rgba(14,14,18,0.96));
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
    animation: ${scan} 9s linear infinite;
  }
`;

const HeroStack = styled.div`
  display: grid;
  gap: 10px;
  align-content: start;
`;

const DataPlate = styled.div`
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.02);
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const BigLabel = styled.div`
  color: ${INK};
  font-size: clamp(1.8rem, 3vw, 3.1rem);
  line-height: 0.9;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const TinyBlock = styled.div`
  color: ${DIM};
  font-size: 0.68rem;
  line-height: 1.55;
  font-weight: 700;
  font-family: 'Courier New', monospace;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const InfoCell = styled.div`
  border: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.015);
  padding: 8px;
  display: grid;
  gap: 3px;
`;

const InfoLabel = styled.div`
  color: ${DIM};
  font-size: 0.58rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 900;
`;

const InfoValue = styled.div`
  color: ${INK};
  font-size: 0.84rem;
  font-weight: 900;
`;

const InlinePills = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Pill = styled.div<{ $alert?: boolean }>`
  padding: 4px 7px;
  border: 1px solid ${({ $alert }) => ($alert ? 'rgba(255,142,142,0.46)' : FAINT)};
  background: rgba(255,255,255,0.02);
  color: ${({ $alert }) => ($alert ? RED : INK)};
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
`;

const MeterGrid = styled.div`
  display: grid;
  gap: 8px;
`;

const MeterRow = styled.div`
  display: grid;
  gap: 6px;
`;

const MeterTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const MeterLabel = styled.div`
  color: ${INK};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
`;

const MeterMeta = styled.div`
  color: ${DIM};
  font-size: 0.66rem;
  font-weight: 800;
`;

const MeterTrack = styled.div`
  position: relative;
  height: 8px;
  background: rgba(255,255,255,0.05);
`;

const MeterFill = styled.div<{ $width: number; $color?: string }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $color }) => $color ?? INK};
`;

const Ledger = styled.div`
  display: grid;
  gap: 8px;
`;

const LedgerRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  padding-bottom: 7px;
  border-bottom: 1px dotted rgba(255,255,255,0.09);
`;

const LedgerKey = styled.div`
  color: ${DIM};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LedgerValue = styled.div<{ $alert?: boolean }>`
  color: ${({ $alert }) => ($alert ? RED : INK)};
  font-size: 0.8rem;
  font-weight: 900;
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 1120px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatPlate = styled.div`
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.015);
  padding: 10px;
  display: grid;
  gap: 6px;
`;

const StatLabel = styled.div`
  color: ${DIM};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 900;
`;

const StatValue = styled.div`
  color: ${INK};
  font-size: clamp(1.1rem, 2vw, 2rem);
  line-height: 0.94;
  font-weight: 900;
`;

const StatMeta = styled.div`
  color: ${DIM};
  font-size: 0.68rem;
  line-height: 1.45;
  font-weight: 700;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Legend = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${DIM};
  font-size: 0.68rem;
  text-transform: uppercase;
  font-weight: 800;
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const ChartFrame = styled.div`
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.015);
  padding: 10px;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 250px;
  display: block;
`;

const AxisRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  color: ${DIM};
  font-size: 0.6rem;
  text-transform: uppercase;
  font-weight: 800;
  letter-spacing: 0.12em;
`;

const List = styled.div`
  display: grid;
  gap: 8px;
  max-height: 330px;
  overflow: auto;
`;

const RowCard = styled.div`
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.015);
  padding: 9px;
  display: grid;
  gap: 6px;
`;

const RowTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const RowTitle = styled.div`
  color: ${INK};
  font-size: 0.74rem;
  font-weight: 900;
`;

const RowCopy = styled.div`
  color: ${DIM};
  font-size: 0.67rem;
  line-height: 1.45;
  font-weight: 700;
`;

const MiniBadge = styled.div<{ $alert?: boolean }>`
  padding: 4px 6px;
  border: 1px solid ${({ $alert }) => ($alert ? 'rgba(255,142,142,0.46)' : FAINT)};
  color: ${({ $alert }) => ($alert ? RED : INK)};
  font-size: 0.6rem;
  text-transform: uppercase;
  font-weight: 800;
  letter-spacing: 0.08em;
`;

const DataTableWrap = styled.div`
  overflow: auto;
  max-height: 430px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1180px;
  border-collapse: collapse;

  th,
  td {
    white-space: nowrap;
    text-align: left;
    padding: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: rgba(10,10,12,0.98);
    color: ${DIM};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
  }

  td {
    color: ${INK};
    font-size: 0.72rem;
    font-weight: 700;
  }
`;

const TicketId = styled.span`
  color: ${CYAN};
  font-weight: 900;
`;

const StatusTag = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  text-transform: uppercase;
  font-weight: 800;
  color: ${INK};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`;

const PriorityTag = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  border: 1px dashed ${FAINT};
  padding: 18px;
  color: ${DIM};
  font-size: 0.76rem;
  text-align: center;
  font-weight: 700;
`;
function readableStatus(value: string) {
  return value.replace(/_/g, ' ');
}

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatCompact(value: number) {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatHours(value: number) {
  return `${value.toFixed(1)}h`;
}

function formatMinutesAsHours(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '--';
  return formatHours(Math.max(0, value) / 60);
}

function formatDateTime(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString();
}

function formatRange(startAt?: string, endAt?: string) {
  if (!startAt || !endAt) return '--';
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '--';
  return `${start.toLocaleString()} - ${end.toLocaleString()}`;
}

function buildOverviewPath(filters: {
  timeframe: OverviewTimeframe;
  orgId: string;
  teamId: string;
  projectId: string;
}) {
  const params = new URLSearchParams();
  params.set('timeframe', filters.timeframe);
  if (filters.orgId) params.set('orgId', filters.orgId);
  if (filters.teamId) params.set('teamId', filters.teamId);
  if (filters.projectId) params.set('projectId', filters.projectId);
  return `/api/team/overview?${params.toString()}`;
}

function buildLinePath(values: number[], width: number, height: number, paddingX: number, paddingY: number) {
  if (!values.length) return '';
  const max = Math.max(1, ...values);
  const innerWidth = width - paddingX * 2;
  const innerHeight = height - paddingY * 2;
  return values
    .map((value, index) => {
      const x = paddingX + (values.length === 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
      const y = height - paddingY - (value / max) * innerHeight;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

function buildAreaPath(values: number[], width: number, height: number, paddingX: number, paddingY: number) {
  if (!values.length) return '';
  const path = buildLinePath(values, width, height, paddingX, paddingY);
  const innerWidth = width - paddingX * 2;
  const lastX = paddingX + (values.length === 1 ? innerWidth / 2 : innerWidth);
  return `${path} L ${lastX} ${height - paddingY} L ${paddingX} ${height - paddingY} Z`;
}

function ThroughputChart({ points }: { points: TimeseriesEntry[] }) {
  const width = 860;
  const height = 250;
  const paddingX = 24;
  const paddingY = 22;
  const created = points.map((item) => item.created);
  const closed = points.map((item) => item.closed);
  const touched = points.map((item) => item.touched);
  const labels = points.filter((_, index) => index % Math.max(1, Math.ceil(points.length / 6)) === 0).slice(0, 6);
  const peak = Math.max(1, ...created, ...closed, ...touched);

  return (
    <>
      <ChartLegend>
        <Legend><LegendDot $color={INK} />Ingress</Legend>
        <Legend><LegendDot $color={GREEN} />Closure</Legend>
        <Legend><LegendDot $color={CYAN} />Activity</Legend>
      </ChartLegend>
      <ChartFrame>
        <ChartSvg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="throughput-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={INK} stopOpacity="0.18" />
              <stop offset="100%" stopColor={INK} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={width} height={height} fill="rgba(255,255,255,0.01)" />
          {Array.from({ length: 6 }).map((_, index) => {
            const y = paddingY + ((height - paddingY * 2) / 5) * index;
            return <line key={`grid-y-${index}`} x1={paddingX} x2={width - paddingX} y1={y} y2={y} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 8" />;
          })}
          {Array.from({ length: Math.max(points.length, 2) }).map((_, index) => {
            const x = paddingX + (index / Math.max(1, points.length - 1)) * (width - paddingX * 2);
            return <line key={`grid-x-${index}`} x1={x} x2={x} y1={paddingY} y2={height - paddingY} stroke="rgba(255,255,255,0.04)" strokeDasharray="3 10" />;
          })}
          <path d={buildAreaPath(created, width, height, paddingX, paddingY)} fill="url(#throughput-area)" />
          <path d={buildLinePath(created, width, height, paddingX, paddingY)} fill="none" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          <path d={buildLinePath(closed, width, height, paddingX, paddingY)} fill="none" stroke={GREEN} strokeWidth="2.1" strokeLinejoin="round" strokeLinecap="round" />
          <path d={buildLinePath(touched, width, height, paddingX, paddingY)} fill="none" stroke={CYAN} strokeWidth="2.1" strokeLinejoin="round" strokeLinecap="round" />
          <text x={paddingX} y={paddingY - 6} fill={DIM} fontSize="11" fontWeight="700">PEAK {formatCompact(peak)}</text>
        </ChartSvg>
      </ChartFrame>
      <AxisRow>
        {labels.map((point) => (
          <span key={point.key}>{point.label}</span>
        ))}
      </AxisRow>
    </>
  );
}

function WireDiagram({ nodes }: { nodes: WireNode[] }) {
  const groupRef = useRef<THREE.Group | null>(null);
  const coreRef = useRef<THREE.Mesh | null>(null);

  useFrame((state, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += delta * 0.08;
    if (coreRef.current) {
      const s = 1 + Math.sin(state.clock.elapsedTime * 1.4) * 0.03;
      coreRef.current.scale.setScalar(s);
    }
  });

  return (
    <group ref={groupRef} position={[0, -0.15, 0]}>
      <mesh ref={coreRef} position={[0, 0.1, 0]}>
        <icosahedronGeometry args={[0.64, 1]} />
        <meshBasicMaterial color={INK} wireframe />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.24, 0]}>
        <torusGeometry args={[1.35, 0.03, 10, 64]} />
        <meshBasicMaterial color={DIM} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.44, 0]}>
        <torusGeometry args={[3.12, 0.02, 10, 80]} />
        <meshBasicMaterial color={FAINT} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.15, 0]}>
        <planeGeometry args={[13, 13, 18, 18]} />
        <meshBasicMaterial color="#666" wireframe transparent opacity={0.18} />
      </mesh>
      {nodes.map((node) => (
        <group key={node.id}>
          <Line points={[[0, 0.1, 0], node.position]} color={node.color} transparent opacity={0.36} />
          <mesh position={node.position}>
            <sphereGeometry args={[node.scale, 18, 18]} />
            <meshBasicMaterial color={node.color} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function WireDiagramScene({ nodes }: { nodes: WireNode[] }) {
  return (
    <Canvas dpr={[1, 1.4]} camera={{ position: [0, 4.8, 8.8], fov: 42 }}>
      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 7, 5]} intensity={1} color="#ffffff" />
      <directionalLight position={[-4, 4, -4]} intensity={0.25} color="#ffffff" />
      <WireDiagram nodes={nodes} />
    </Canvas>
  );
}
export default function TeamsOverviewPage() {
  const [timeframe, setTimeframe] = useState<OverviewTimeframe>('month');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [overview, setOverview] = useState<TeamOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const path = buildOverviewPath({ timeframe, orgId: selectedOrgId, teamId: selectedTeamId, projectId: selectedProjectId });
        const response = await apiFetch<TeamOverviewResponse>(path, { signal: controller.signal });
        if (disposed) return;
        setOverview(response);
      } catch (err: any) {
        if (disposed || err?.name === 'AbortError') return;
        setError(err?.message || 'Failed to load team overview analytics.');
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    void run();
    return () => {
      disposed = true;
      controller.abort();
    };
  }, [timeframe, selectedOrgId, selectedTeamId, selectedProjectId]);

  const orgOptions = useMemo(() => overview?.scope.orgs ?? [], [overview?.scope.orgs]);
  const teamOptions = useMemo(() => overview?.distributions.teams ?? [], [overview?.distributions.teams]);
  const projectOptions = useMemo(() => overview?.distributions.projects ?? [], [overview?.distributions.projects]);

  const systemState = useMemo(() => {
    if (!overview) return { label: 'System idle', alert: false };
    const score = overview.summary.slaBreachedOpen * 4 + overview.summary.slaAtRisk24h * 2 + overview.summary.reopenCount + overview.summary.noShowEvents;
    if (score >= 18) return { label: 'Critical pressure', alert: true };
    if (score >= 6) return { label: 'Watch state', alert: false };
    return { label: 'Nominal flow', alert: false };
  }, [overview]);

  const infoCells = useMemo(() => {
    if (!overview) return [];
    return [
      { label: 'Total tickets', value: formatCompact(overview.summary.totalTickets) },
      { label: 'Active load', value: formatCompact(overview.summary.activeTickets) },
      { label: 'Tracked active', value: formatMinutesAsHours(overview.summary.trackedActiveMinutes) },
      { label: 'Avg active resolution', value: formatHours(overview.summary.avgActiveResolutionHours) },
    ];
  }, [overview]);

  const summaryStrip = useMemo(() => {
    if (!overview) return [];
    return [
      {
        label: 'Monthly contract',
        value:
          overview.summary.contractedMonthlyMinutesMin === overview.summary.contractedMonthlyMinutesMax
            ? formatMinutesAsHours(overview.summary.contractedMonthlyMinutesMax)
            : `${formatMinutesAsHours(overview.summary.contractedMonthlyMinutesMin)} - ${formatMinutesAsHours(overview.summary.contractedMonthlyMinutesMax)}`,
        meta: 'contractual monthly delivery baseline',
      },
      {
        label: 'Remaining contract',
        value:
          overview.summary.remainingContractedMinutesMin === overview.summary.remainingContractedMinutesMax
            ? formatMinutesAsHours(overview.summary.remainingContractedMinutesMax)
            : `${formatMinutesAsHours(overview.summary.remainingContractedMinutesMin)} - ${formatMinutesAsHours(overview.summary.remainingContractedMinutesMax)}`,
        meta: 'monthly billable headroom after tracked active work',
      },
      {
        label: 'Remaining estimate',
        value: formatMinutesAsHours(overview.summary.totalRemainingEstimateMinutes),
        meta: `${formatMinutesAsHours(overview.summary.totalEstimatedMinutes)} total estimated workload`,
      },
      {
        label: 'Operator capacity',
        value: formatMinutesAsHours(overview.summary.operatorCapacityMinutes),
        meta: `${formatMinutesAsHours(overview.summary.operatorBookedMinutes)} booked | ${formatMinutesAsHours(overview.summary.operatorUnbookedMinutes)} unbooked`,
      },
    ];
  }, [overview]);

  const riskLedger = useMemo(() => {
    if (!overview) return [];
    return [
      { key: '01', label: 'Open breach stack', value: overview.summary.slaBreachedOpen, alert: overview.summary.slaBreachedOpen > 0 },
      { key: '02', label: 'Blocked workload', value: Math.round((overview.summary.trackedBlockedMinutes || 0) / 60 * 10) / 10, alert: overview.summary.trackedBlockedMinutes > 0 },
      { key: '03', label: 'Outside goal', value: overview.summary.outsideTargetCount, alert: overview.summary.outsideTargetCount > 0 },
      { key: '04', label: '24h risk window', value: overview.summary.slaAtRisk24h, alert: overview.summary.slaAtRisk24h > 0 },
      { key: '05', label: 'Reopen count', value: overview.summary.reopenCount, alert: false },
    ];
  }, [overview]);

  const routeNodes = useMemo<WireNode[]>(() => {
    if (!overview) return [];
    const teams = overview.distributions.teams.slice(0, 4);
    const projects = overview.distributions.projects.slice(0, 4);
    const agents = overview.distributions.agents.slice(0, 5);
    const list: WireNode[] = [];

    teams.forEach((team, index) => {
      const angle = (index / Math.max(1, teams.length)) * Math.PI * 2;
      list.push({
        id: `t-${team.id}`,
        position: [Math.cos(angle) * 2.1, 0.15 + (index % 2) * 0.32, Math.sin(angle) * 2.1],
        color: INK,
        scale: 0.12 + clamp(team.total / Math.max(1, teams[0]?.total ?? 1)) * 0.14,
      });
    });

    projects.forEach((project, index) => {
      const angle = (index / Math.max(1, projects.length)) * Math.PI * 2 + 0.48;
      list.push({
        id: `p-${project.id}`,
        position: [Math.cos(angle) * 3.7, -0.05 + (index % 2) * 0.22, Math.sin(angle) * 3.7],
        color: AMBER,
        scale: 0.1 + clamp(project.total / Math.max(1, projects[0]?.total ?? 1)) * 0.12,
      });
    });

    agents.forEach((agent, index) => {
      const angle = (index / Math.max(1, agents.length)) * Math.PI * 2 + 0.92;
      list.push({
        id: `a-${agent.userId}`,
        position: [Math.cos(angle) * 5.1, -0.22 + (index % 2) * 0.22, Math.sin(angle) * 5.1],
        color: agent.breached > 0 ? RED : CYAN,
        scale: 0.09 + clamp(agent.total / Math.max(1, agents[0]?.total ?? 1)) * 0.11,
      });
    });

    return list;
  }, [overview]);

  const teamMeters = useMemo<MeterItem[]>(() => (overview?.distributions.teams ?? []).slice(0, 5).map((team) => ({
    key: team.id,
    label: team.label,
    value: team.total,
    meta: `${team.members} members | ${formatMinutesAsHours(team.remainingEstimateMinutes)} queued`,
  })), [overview?.distributions.teams]);

  const projectMeters = useMemo<MeterItem[]>(() => (overview?.distributions.projects ?? []).slice(0, 5).map((project) => ({
    key: project.id,
    label: project.label,
    value: project.total,
    meta: `${formatMinutesAsHours(project.remainingEstimateMinutes)} queued | ${formatMinutesAsHours(project.activeMinutes)} active`,
    color: project.breached > 0 ? RED : AMBER,
  })), [overview?.distributions.projects]);

  const agentMeters = useMemo<MeterItem[]>(() => (overview?.distributions.agents ?? []).slice(0, 6).map((agent) => ({
    key: agent.userId,
    label: agent.name,
    value: agent.total,
    meta: `${formatMinutesAsHours(agent.remainingEstimateMinutes)} queued | ${formatHours(agent.avgActiveResolutionHours)} avg active`,
    color: agent.breached > 0 ? RED : CYAN,
  })), [overview?.distributions.agents]);

  const statusMeters = useMemo<MeterItem[]>(() => (overview?.distributions.status ?? []).map((item) => ({
    key: item.key,
    label: readableStatus(item.label),
    value: item.value,
    color: STATUS_COLORS[item.key] ?? INK,
  })), [overview?.distributions.status]);

  const serviceMeters = useMemo<MeterItem[]>(() => (overview?.distributions.services ?? []).slice(0, 5).map((item) => ({
    key: item.key,
    label: item.label,
    value: item.value,
  })), [overview?.distributions.services]);

  const requestCategoryMeters = useMemo<MeterItem[]>(() => (overview?.distributions.requestCategories ?? []).slice(0, 5).map((item) => ({
    key: item.key,
    label: item.label,
    value: item.value,
  })), [overview?.distributions.requestCategories]);

  return (
    <Page>
      <Board>
        <Header>
          <Kicker>cybard tactical dossier</Kicker>
          <HeaderRow>
            <Title>Teams Overview</Title>
            <HeaderRail>
              <Chip $active>{systemState.label}</Chip>
              <Chip>{overview?.range.bucketUnit ?? '--'} bucket</Chip>
              <Chip $alert={Boolean(error)}>{error ? 'Link degraded' : 'Data live'}</Chip>
            </HeaderRail>
          </HeaderRow>
          <Subtitle>
            A monochrome operational board for scoped routing pressure, throughput, SLA exposure, and ownership concentration.
            The backend contract remains unchanged; only the frontend composition is being re-cast into a dossier-style system sheet.
          </Subtitle>
        </Header>

        <ControlBar>
          <ControlTop>
            <ControlTitle>Scope arbitration</ControlTitle>
            <TimeframeRow>
              {TIMEFRAME_OPTIONS.map((option) => (
                <TimeframeButton
                  key={option.value}
                  type="button"
                  $active={timeframe === option.value}
                  onClick={() => setTimeframe(option.value)}
                >
                  {option.label}
                </TimeframeButton>
              ))}
            </TimeframeRow>
          </ControlTop>

          <FilterGrid>
            <Field>
              <FieldLabel>Organization</FieldLabel>
              <FieldSelect value={selectedOrgId} onChange={(event) => setSelectedOrgId(event.target.value)}>
                <option value="">All organizations</option>
                {orgOptions.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </FieldSelect>
            </Field>
            <Field>
              <FieldLabel>Team</FieldLabel>
              <FieldSelect value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
                <option value="">All teams</option>
                {teamOptions.map((team) => (
                  <option key={team.id} value={team.id}>{team.label}</option>
                ))}
              </FieldSelect>
            </Field>
            <Field>
              <FieldLabel>Project</FieldLabel>
              <FieldSelect value={selectedProjectId} onChange={(event) => setSelectedProjectId(event.target.value)}>
                <option value="">All projects</option>
                {projectOptions.map((project) => (
                  <option key={project.id} value={project.id}>{project.label}</option>
                ))}
              </FieldSelect>
            </Field>
          </FilterGrid>

          <MetaRow>
            <MetaBox>
              <strong>Range</strong>
              {overview ? formatRange(overview.range.startAt, overview.range.endAt) : '--'}
            </MetaBox>
            <MetaBox>
              <strong>Scope</strong>
              {(selectedOrgId ? orgOptions.find((org) => org.id === selectedOrgId)?.name : 'All organizations') || 'All organizations'} / {(selectedTeamId ? teamOptions.find((team) => team.id === selectedTeamId)?.label : 'All teams') || 'All teams'} / {(selectedProjectId ? projectOptions.find((project) => project.id === selectedProjectId)?.label : 'All projects') || 'All projects'}
            </MetaBox>
            <MetaBox>
              <strong>Timezone</strong>
              {overview?.range.timezone ?? 'UTC'}
            </MetaBox>
          </MetaRow>
        </ControlBar>

        {loading ? <Notice>Synchronizing scoped telemetry...</Notice> : null}
        {error ? <Notice $alert>{error}</Notice> : null}

        <Grid>
          <Plate $col="1 / span 7" $row="span 2" $min="520px">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>section 0 // command board</PlateCode>
                <PlateTitle>Operational Dossier</PlateTitle>
                <PlateCopy>Interlinked route graph, scope identity, and top-line operational characteristics.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>A-02</PlateIndex>
            </PlateHead>
            <PlateBody>
              <HeroLayout>
                <ScenePanel>
                  {routeNodes.length ? <WireDiagramScene nodes={routeNodes} /> : <EmptyState>No scoped route graph.</EmptyState>}
                </ScenePanel>
                <HeroStack>
                  <DataPlate>
                    <PlateCode>identity</PlateCode>
                    <BigLabel>OPS / TEAM</BigLabel>
                    <TinyBlock>
                      RANGE :: {overview ? formatRange(overview.range.startAt, overview.range.endAt) : '--'}
                      {'\n'}ORG COUNT :: {overview?.scope.orgIds.length ?? 0}
                      {'\n'}TIMEFRAME :: {timeframe.toUpperCase()}
                    </TinyBlock>
                  </DataPlate>

                  <InfoGrid>
                    {infoCells.map((cell) => (
                      <InfoCell key={cell.label}>
                        <InfoLabel>{cell.label}</InfoLabel>
                        <InfoValue>{cell.value}</InfoValue>
                      </InfoCell>
                    ))}
                  </InfoGrid>

                  <InlinePills>
                    <Pill>{overview?.summary.activeAgents ?? 0} active agents</Pill>
                    <Pill>{overview?.summary.activeProjects ?? 0} active projects</Pill>
                    <Pill>{overview ? formatMinutesAsHours(overview.summary.totalRemainingEstimateMinutes) : '0.0h'} queued</Pill>
                    <Pill $alert={(overview?.summary.outsideTargetCount ?? 0) > 0}>{overview?.summary.outsideTargetCount ?? 0} outside goal</Pill>
                  </InlinePills>
                </HeroStack>
              </HeroLayout>
            </PlateBody>
          </Plate>

          <Plate $col="8 / span 5">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>risk // live readout</PlateCode>
                <PlateTitle>Pressure Ledger</PlateTitle>
                <PlateCopy>Fast tactical read of the current breach and risk stack.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>R-01</PlateIndex>
            </PlateHead>
            <PlateBody>
              <Ledger>
                {riskLedger.length ? riskLedger.map((item) => (
                  <LedgerRow key={item.key}>
                    <LedgerKey>{item.key} / {item.label}</LedgerKey>
                    <LedgerValue $alert={item.alert}>{formatNumber(item.value)}</LedgerValue>
                  </LedgerRow>
                )) : <EmptyState>No risk telemetry.</EmptyState>}
              </Ledger>
            </PlateBody>
          </Plate>

          <Plate $col="8 / span 5">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>summary // cadence</PlateCode>
                <PlateTitle>Mission Summary Strip</PlateTitle>
                <PlateCopy>High-level operational markers for queue pressure, meetings, and delivery.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>S-07</PlateIndex>
            </PlateHead>
            <PlateBody>
              <SummaryStrip>
                {summaryStrip.length ? summaryStrip.map((item) => (
                  <StatPlate key={item.label}>
                    <StatLabel>{item.label}</StatLabel>
                    <StatValue>{item.value}</StatValue>
                    <StatMeta>{item.meta}</StatMeta>
                  </StatPlate>
                )) : <EmptyState>No summary telemetry.</EmptyState>}
              </SummaryStrip>
            </PlateBody>
          </Plate>

          <Plate $col="1 / span 7">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>signal // ingress-closure ribbon</PlateCode>
                <PlateTitle>Throughput Trace</PlateTitle>
                <PlateCopy>Intake, closure, and activity traces styled as a monochrome system signal panel.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>T-11</PlateIndex>
            </PlateHead>
            <PlateBody>
              {(overview?.timeseries.length ?? 0) > 0 ? <ThroughputChart points={overview?.timeseries ?? []} /> : <EmptyState>No throughput timeseries in current scope.</EmptyState>}
            </PlateBody>
          </Plate>

          <Plate $col="8 / span 5">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>status // stack</PlateCode>
                <PlateTitle>State Distribution</PlateTitle>
                <PlateCopy>Status pressure, request-category mix, and service concentration rendered as segmented meters.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>D-04</PlateIndex>
            </PlateHead>
            <PlateBody>
              <MeterGrid>
                {statusMeters.map((item) => {
                  const max = Math.max(1, ...statusMeters.map((entry) => entry.value));
                  return (
                    <MeterRow key={item.key}>
                      <MeterTop>
                        <MeterLabel>{item.label}</MeterLabel>
                        <MeterMeta>{formatNumber(item.value)}</MeterMeta>
                      </MeterTop>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={item.color} />
                      </MeterTrack>
                    </MeterRow>
                  );
                })}
                {requestCategoryMeters.slice(0, 3).map((item) => {
                  const max = Math.max(1, ...requestCategoryMeters.map((entry) => entry.value));
                  return (
                    <MeterRow key={item.key}>
                      <MeterTop>
                        <MeterLabel>{item.label}</MeterLabel>
                        <MeterMeta>{formatNumber(item.value)}</MeterMeta>
                      </MeterTop>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={SOFT} />
                      </MeterTrack>
                    </MeterRow>
                  );
                })}
                {serviceMeters.slice(0, 2).map((item) => {
                  const max = Math.max(1, ...serviceMeters.map((entry) => entry.value));
                  return (
                    <MeterRow key={`service-${item.key}`}>
                      <MeterTop>
                        <MeterLabel>{item.label}</MeterLabel>
                        <MeterMeta>{formatNumber(item.value)}</MeterMeta>
                      </MeterTop>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={DIM} />
                      </MeterTrack>
                    </MeterRow>
                  );
                })}
              </MeterGrid>
            </PlateBody>
          </Plate>

          <Plate $col="1 / span 4">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>routing // team load</PlateCode>
                <PlateTitle>Teams</PlateTitle>
                <PlateCopy>Routed team concentration with queued work and roster capacity.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>L-01</PlateIndex>
            </PlateHead>
            <PlateBody>
              <List>
                {teamMeters.length ? teamMeters.map((item) => {
                  const max = Math.max(1, ...teamMeters.map((entry) => entry.value));
                  return (
                    <RowCard key={item.key}>
                      <RowTop>
                        <RowTitle>{item.label}</RowTitle>
                        <MiniBadge>{formatNumber(item.value)}</MiniBadge>
                      </RowTop>
                      <RowCopy>{item.meta}</RowCopy>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={INK} />
                      </MeterTrack>
                    </RowCard>
                  );
                }) : <EmptyState>No team routing data.</EmptyState>}
              </List>
            </PlateBody>
          </Plate>

          <Plate $col="5 / span 4">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>routing // project load</PlateCode>
                <PlateTitle>Projects</PlateTitle>
                <PlateCopy>Scoped project concentration with active work and queued effort markers.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>L-02</PlateIndex>
            </PlateHead>
            <PlateBody>
              <List>
                {projectMeters.length ? projectMeters.map((item) => {
                  const max = Math.max(1, ...projectMeters.map((entry) => entry.value));
                  return (
                    <RowCard key={item.key}>
                      <RowTop>
                        <RowTitle>{item.label}</RowTitle>
                        <MiniBadge $alert={item.color === RED}>{formatNumber(item.value)}</MiniBadge>
                      </RowTop>
                      <RowCopy>{item.meta}</RowCopy>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={item.color ?? AMBER} />
                      </MeterTrack>
                    </RowCard>
                  );
                }) : <EmptyState>No project routing data.</EmptyState>}
              </List>
            </PlateBody>
          </Plate>

          <Plate $col="9 / span 4">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>ownership // agent stack</PlateCode>
                <PlateTitle>Agents</PlateTitle>
                <PlateCopy>Ownership distribution, queued effort, and active-resolution readout.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>L-03</PlateIndex>
            </PlateHead>
            <PlateBody>
              <List>
                {agentMeters.length ? agentMeters.map((item) => {
                  const max = Math.max(1, ...agentMeters.map((entry) => entry.value));
                  const alert = item.color === RED;
                  return (
                    <RowCard key={item.key}>
                      <RowTop>
                        <RowTitle>{item.label}</RowTitle>
                        <MiniBadge $alert={alert}>{formatNumber(item.value)}</MiniBadge>
                      </RowTop>
                      <RowCopy>{item.meta}</RowCopy>
                      <MeterTrack>
                        <MeterFill $width={clamp(item.value / max) * 100} $color={item.color ?? CYAN} />
                      </MeterTrack>
                    </RowCard>
                  );
                }) : <EmptyState>No agent ownership data.</EmptyState>}
              </List>
            </PlateBody>
          </Plate>

          <Plate $col="1 / span 12">
            <PlateHead>
              <PlateTitleWrap>
                <PlateCode>sample // scoped ticket manifest</PlateCode>
                <PlateTitle>Ticket Intelligence Table</PlateTitle>
                <PlateCopy>Direct sample of the scoped workload with status, priority, ownership, and queue weight.</PlateCopy>
              </PlateTitleWrap>
              <PlateIndex>TABLE-0</PlateIndex>
            </PlateHead>
            <PlateBody>
              <DataTableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>Title</th>
                      <th>Request category</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Organization</th>
                      <th>Project</th>
                      <th>Team</th>
                      <th>Assigned</th>
                      <th>Estimate</th>
                      <th>Active</th>
                      <th>Blocked</th>
                      <th>Goal</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview?.ticketsSample ?? []).length ? (
                      (overview?.ticketsSample ?? []).map((ticket) => (
                        <tr key={ticket.id}>
                          <td><TicketId>{ticket.ticketNumber ? `#${ticket.ticketNumber}` : ticket.id.slice(0, 8)}</TicketId></td>
                          <td>{ticket.title || 'Untitled ticket'}</td>
                          <td>{ticket.requestCategoryName || '--'}</td>
                          <td><StatusTag $color={STATUS_COLORS[ticket.status] ?? INK}>{readableStatus(ticket.status)}</StatusTag></td>
                          <td><PriorityTag $color={PRIORITY_COLORS[ticket.priority] ?? INK}>{ticket.priority}</PriorityTag></td>
                          <td>{ticket.orgName}</td>
                          <td>{ticket.projectName || '--'}</td>
                          <td>{ticket.teamLabel || 'Unrouted'}</td>
                          <td>{ticket.assignedAgentName || '--'}</td>
                          <td>{formatMinutesAsHours(ticket.estimatedMinutes)}</td>
                          <td>{formatMinutesAsHours(ticket.actualActiveMinutes)}</td>
                          <td>{formatMinutesAsHours(ticket.blockedMinutes)}</td>
                          <td>{formatMinutesAsHours(ticket.targetCompletionMinutes)}</td>
                          <td title={formatDateTime(ticket.updatedAt)}>{formatDateTime(ticket.updatedAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={14}><EmptyState>No ticket sample rows in current scope.</EmptyState></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </DataTableWrap>
            </PlateBody>
          </Plate>
        </Grid>
      </Board>
    </Page>
  );
}
